import {
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  CurrencyEuroIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  InboxIcon,
  LightBulbIcon,
  PlayIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { Role } from '@whoworks/shared';
import type { ComponentType, SVGProps } from 'react';

/**
 * Informačná architektúra appky na jednom mieste — bočný panel, spodná lišta
 * aj list „Ďalšie“ čítajú odtiaľto, takže poradie a názvy nejdú od seba.
 *
 * - **Dnes**: čo človek otvára každé ráno (osobné).
 * - **Tím**: čo tím spolu drží.
 * - **Projekty**: zoznam projektov sám o sebe, s farbou a stavom.
 *
 * Zoznam všetkých projektov (`/projects`) nie je položka v skupine Tím — je to
 * nadpis skupiny Projekty. Dve položky s rovnakým menom pod sebou mýlili.
 */

export type NavBadge = 'chat' | 'inbox';

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Ktoré počítadlo nesie (neprečítané správy, schránka). */
  badge?: NavBadge;
};

export const NAV_TODAY: readonly NavItem[] = [
  { to: '/', label: 'Domov', icon: HomeIcon },
  { to: '/run', label: 'Spustenie', icon: PlayIcon },
  { to: '/plan', label: 'Plán', icon: CalendarDaysIcon },
  { to: '/chat', label: 'Chat', icon: ChatBubbleLeftRightIcon, badge: 'chat' },
  { to: '/schranka', label: 'Schránka', icon: InboxIcon, badge: 'inbox' },
];

export const NAV_TEAM: readonly NavItem[] = [
  { to: '/team', label: 'Tím', icon: UsersIcon },
  { to: '/ideas', label: 'Ideas', icon: LightBulbIcon },
  { to: '/produkty', label: 'Produkty', icon: ChartBarSquareIcon },
  { to: '/notes', label: 'Poznámky', icon: DocumentTextIcon },
  { to: '/expenses', label: 'Výdavky', icon: CurrencyEuroIcon },
];

/**
 * Spodná lišta na telefóne: štyri ciele + „Ďalšie“. Viac ako päť položiek sa
 * na šírku iPhonu nezmestí s čitateľným popisom a 44 px cieľom.
 *
 * Piata položka sa volá „Ďalšie“, nie „Viac“: „Viac“ je už rozbaľovacia
 * záložka v detaile projektu (Vydania, Súbory, …) a dve rôzne „Viac“ na jednej
 * obrazovke — jedno hore v projekte, jedno dole v lište — nebolo možné
 * rozlíšiť.
 */
export const TAB_BAR_ITEMS: readonly NavItem[] = NAV_TODAY.filter((item) => item.to !== '/schranka');
export const TAB_BAR_MORE_LABEL = 'Ďalšie';

/** Čo je v liste „Ďalšie“: zvyšok skupiny Dnes a celá skupina Tím. */
export const MORE_SHEET_ITEMS: readonly NavItem[] = [
  ...NAV_TODAY.filter((item) => !TAB_BAR_ITEMS.includes(item)),
  { to: '/projects', label: 'Projekty', icon: FolderIcon },
  ...NAV_TEAM,
];

/** Odznak s počtom — nad 99 sa už nepočíta, iba ukazuje, že je toho veľa. */
export const formatBadge = (count: number): string => (count > 99 ? '99+' : String(count));

/**
 * Hosť (rola `guest`) je človek zvonka pozvaný do jedného projektu. Tím, plán,
 * výdavky, nápady ani produkty preňho nie sú — API mu ich aj tak nevydá
 * (`apps/api/src/lib/guest-policy.ts`), takže položky, ktoré by viedli na
 * prázdnu obrazovku alebo chybu, sa mu ani neukazujú. Chat je iba kanál jeho
 * projektov.
 */
export const GUEST_NAV: readonly NavItem[] = [
  { to: '/projects', label: 'Projekty', icon: FolderIcon },
  { to: '/chat', label: 'Chat', icon: ChatBubbleLeftRightIcon, badge: 'chat' },
  { to: '/schranka', label: 'Schránka', icon: InboxIcon, badge: 'inbox' },
];

export type Navigation = {
  today: readonly NavItem[];
  team: readonly NavItem[];
  tabBar: readonly NavItem[];
  moreSheet: readonly NavItem[];
  /** JARVIS číta dáta celej appky, hosťovi sa neponúka. */
  jarvis: boolean;
  /** Skupina „Projekty" s ich zoznamom v bočnom paneli. */
  projects: boolean;
};

const TEAM_NAVIGATION: Navigation = {
  today: NAV_TODAY,
  team: NAV_TEAM,
  tabBar: TAB_BAR_ITEMS,
  moreSheet: MORE_SHEET_ITEMS,
  jarvis: true,
  projects: true,
};

const GUEST_NAVIGATION: Navigation = {
  today: GUEST_NAV,
  team: [],
  tabBar: GUEST_NAV,
  moreSheet: [],
  jarvis: false,
  projects: true,
};

export const navigationFor = (role: Role | null | undefined): Navigation =>
  role === 'guest' ? GUEST_NAVIGATION : TEAM_NAVIGATION;

/**
 * Obrazovky, na ktoré hosť smie. Ostatné adresy ho vrátia na Domov (ten ho
 * pošle do jeho projektu) — aj keď ich napíše ručne alebo príde zo starého
 * odkazu. Nastavenia má iba svoje: upozornenia, vzhľad, prihlásenia.
 */
export const GUEST_PATHS: readonly RegExp[] = [
  /^\/$/,
  /^\/projects$/,
  /^\/projects\/\d+$/,
  /^\/chat$/,
  /^\/schranka$/,
  /^\/settings$/,
  /^\/settings\/(notifikacie|aktualizacie|vzhlad|prihlasenia|diagnostika)$/,
];

export const isGuestPath = (pathname: string): boolean => GUEST_PATHS.some((re) => re.test(pathname));
